import { Box, Button, Stack, Tooltip, Typography } from '@mui/material'
import { FC, useCallback } from 'react'
import EditIcon from '../../../../assets/images/edit-pencil.svg?react'
import LocationWarningIcon from '../../../../assets/images/location-warning.svg?react'
import { Grid } from '@mui/system'
import { MISSING_VALUE, WRITE_LOCATION_ADDRESS_SCOPE } from '../../../../constants'
import { useAuthenticationContext } from '../../../../auth/AuthenticationContextProvider'
import { useSetAtom } from 'jotai'
import { addressValidationResultAtom, currentLocationChangeAtom, selectedAddressValidationResultIndexAtom } from '../../../../atoms'
import { addressValidationPaths } from '../../../address-scheduler/addressValidationPaths'
import { useNavigate } from 'react-router-dom'
import { GBAddressCountry, Location, USAddressCountry } from '../../../../api/models'
import { useMonitoringServiceProviderLocation } from '../../../../api/apiQueries'
import { toMonitoringLocationChangeAddressFromLocationAddress } from '../../../address-scheduler/AddressSchedulerFormPage/addressScheduleFormUtils'
import { usePendingWorkflowStatus } from './usePendingWorkflowStatus'
import { StatusChip } from './StatusChip'
import { getAddressDifferences, isProfessionallyMonitored } from './locationUtils'

interface LocationInfoProps {
  location: Location
}

function getTooltip(mspIdentifier: string, mspValue: string | undefined | null): string {
  if (!mspValue) {
    return `${mspIdentifier} does not have a value for this field`
  }
  return `${mspIdentifier} has this info listed as: ${mspValue}`
}

export const LocationInfo: FC<LocationInfoProps> = ({ location }) => {
  const authContext = useAuthenticationContext()
  const isPendingWorkflow = usePendingWorkflowStatus(location.id)

  const mspLocationQuery = useMonitoringServiceProviderLocation(location.id)
  const mspLocation = mspLocationQuery.data
  const isMspLocationLoading = mspLocationQuery.isLoading || mspLocationQuery.isFetching
  const isGBLocation = location?.address?.country === GBAddressCountry.GB

  const mspAddress = mspLocation?.address
  const mspState = mspAddress?.country === USAddressCountry.US ? mspAddress.state : undefined
  const mspPostalCode = mspAddress?.country === USAddressCountry.US ? mspAddress?.zipCode : mspAddress?.postalCode
  const mspIdentifier = mspLocation?.monitoringServiceProviderId || 'MSP'

  const addressDifferences = getAddressDifferences(location, mspLocation, !isMspLocationLoading)
  const hasStreet1Difference = addressDifferences.street1
  const hasStreet2Difference = addressDifferences.street2
  const hasCityDifference = addressDifferences.city
  const hasStateDifference = addressDifferences.state
  const hasPostalCodeDifference = addressDifferences.postalCode
  const hasCountyDifference = addressDifferences.county
  const hasTimezoneDifference = addressDifferences.timeZone
  const hasCrossStreetDifference = addressDifferences.crossStreet

  const setCurrentLocationAtom = useSetAtom(currentLocationChangeAtom)
  const setAddressValidationAtom = useSetAtom(addressValidationResultAtom)
  const setSelectedAddressValidationResultIndexAtom = useSetAtom(selectedAddressValidationResultIndexAtom)
  const navigate = useNavigate()
  const onEditClick = useCallback(() => {
    if (location && location.address) {
      setCurrentLocationAtom({
        locationId: location.id,
        address: toMonitoringLocationChangeAddressFromLocationAddress(location.address)
      })
      setAddressValidationAtom(undefined)
      setSelectedAddressValidationResultIndexAtom(undefined)
      navigate(`${addressValidationPaths.overview}/${addressValidationPaths.addressForm}`)
    }
  }, [location, navigate, setCurrentLocationAtom, setAddressValidationAtom, setSelectedAddressValidationResultIndexAtom])

  const disabledFieldStyle = !isProfessionallyMonitored(location) ? { color: 'var(--text-disabled)' } : {}

  return (
    <Stack direction="column" sx={{ backgroundColor: 'var(--bg-surface)', borderRadius: '0.5rem', padding: 'var(--spacing-m)' }}>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <Stack direction="column" sx={{ gap: 'var(--spacing-2xs)' }}>
          <Stack direction="row" alignItems="center" sx={{ gap: 'var(--spacing-2xs)' }}>
            <Typography variant={'h2'} fontSize={'var(--size-xl)'} sx={{ fontWeight: '700' }}>
              Location Info
            </Typography>
            {isPendingWorkflow && (
              <StatusChip
                data-testid="location-info-workflow-status-chip"
                status="pending"
                tooltipPlacement="right"
                tooltipText="Updates typically take a few moments"
              />
            )}
          </Stack>
          <Typography sx={{ ...disabledFieldStyle }}>
            This info is used by emergency services to find the customer in the event of an alarm.
          </Typography>
        </Stack>
        {isProfessionallyMonitored(location) && authContext.has(WRITE_LOCATION_ADDRESS_SCOPE) && (
          <Box>
            <Button
              variant="text"
              size="large"
              onClick={onEditClick}
              sx={{
                height: '3.5rem',
                fontSize: 'var(--size-2xs)',
                gap: 'var(--spacing-2xs)'
              }}
              data-testid="edit-location-info-button"
            >
              <Typography>Edit</Typography>
              <EditIcon />
            </Button>
          </Box>
        )}
      </Stack>
      <Grid container spacing={1} sx={{ marginTop: 'var(--spacing-l)', width: '100%' }}>
        {/* First Row: Property Type and (empty) */}
        <Grid size={12}>
          <Stack direction="column" sx={{ gap: 'var(--spacing-2xs)' }}>
            <Typography sx={{ fontWeight: '700', ...disabledFieldStyle }}>Property Type</Typography>
            <Typography sx={{ ...disabledFieldStyle }}>{location?.address?.propertyType || MISSING_VALUE}</Typography>
          </Stack>
        </Grid>
        {/* Second Row: Address and County headers aligned */}
        <Grid size={4}>
          <Stack direction="column" sx={{ gap: 'var(--spacing-2xs)' }}>
            <Typography sx={{ fontWeight: '700', ...disabledFieldStyle }}>Address</Typography>
            <Stack direction="row" alignItems="center" sx={{ gap: 'var(--spacing-2xs)' }}>
              <Typography sx={{ ...disabledFieldStyle }}>{location?.address?.street1 || MISSING_VALUE}</Typography>
              {hasStreet1Difference && (
                <Tooltip title={getTooltip(mspIdentifier, mspLocation?.address.street1)} arrow>
                  <Box sx={{ display: 'flex' }}>
                    <LocationWarningIcon />
                  </Box>
                </Tooltip>
              )}
            </Stack>
            {(hasStreet2Difference || location?.address?.street2) && (
              <Stack direction="row" alignItems="center" sx={{ gap: 'var(--spacing-2xs)' }}>
                <Typography sx={{ ...disabledFieldStyle }}>{location?.address?.street2 || MISSING_VALUE}</Typography>
                {hasStreet2Difference && (
                  <Tooltip title={getTooltip(mspIdentifier, mspLocation?.address.street2)} arrow>
                    <Box sx={{ display: 'flex' }}>
                      <LocationWarningIcon />
                    </Box>
                  </Tooltip>
                )}
              </Stack>
            )}
            <Stack direction="row" alignItems="center" sx={{ gap: 'var(--spacing-2xs)' }}>
              <Typography sx={{ ...disabledFieldStyle }}>{location?.address?.city || MISSING_VALUE}</Typography>
              {hasCityDifference && (
                <Tooltip title={getTooltip(mspIdentifier, mspLocation?.address.city)} arrow>
                  <Box sx={{ display: 'flex' }}>
                    <LocationWarningIcon />
                  </Box>
                </Tooltip>
              )}
            </Stack>
            {!isGBLocation && (
              <Stack direction="row" alignItems="center" sx={{ gap: 'var(--spacing-2xs)' }}>
                <Typography sx={{ ...disabledFieldStyle }}>{location?.address?.state || MISSING_VALUE}</Typography>
                {hasStateDifference && (
                  <Tooltip title={getTooltip(mspIdentifier, mspState)} arrow>
                    <Box sx={{ display: 'flex' }}>
                      <LocationWarningIcon />
                    </Box>
                  </Tooltip>
                )}
              </Stack>
            )}
            <Stack direction="row" alignItems="center" sx={{ gap: 'var(--spacing-2xs)' }}>
              <Typography sx={{ ...disabledFieldStyle }}>{location?.address?.zip || MISSING_VALUE}</Typography>
              {hasPostalCodeDifference && (
                <Tooltip title={getTooltip(mspIdentifier, mspPostalCode)} arrow>
                  <Box sx={{ display: 'flex' }}>
                    <LocationWarningIcon />
                  </Box>
                </Tooltip>
              )}
            </Stack>
          </Stack>
        </Grid>
        <Grid size={4}>
          <Stack direction="column" sx={{ gap: 'var(--spacing-2xs)' }}>
            <Typography sx={{ fontWeight: '700', ...disabledFieldStyle }}>County</Typography>
            <Stack direction="row" alignItems="center" sx={{ gap: 'var(--spacing-2xs)' }}>
              <Typography sx={{ ...disabledFieldStyle }}>{location?.address?.county || MISSING_VALUE}</Typography>
              {hasCountyDifference && (
                <Tooltip title={getTooltip(mspIdentifier, mspLocation?.address.county)} arrow>
                  <Box sx={{ display: 'flex' }}>
                    <LocationWarningIcon />
                  </Box>
                </Tooltip>
              )}
            </Stack>
            <Typography sx={{ fontWeight: '700', ...disabledFieldStyle }}>Time Zone</Typography>
            <Stack direction="row" alignItems="center" sx={{ gap: 'var(--spacing-2xs)' }}>
              <Typography sx={{ ...disabledFieldStyle }}>{location?.address?.timeZone || MISSING_VALUE}</Typography>
              {hasTimezoneDifference && (
                <Tooltip title={getTooltip(mspIdentifier, mspLocation?.address.timeZone)} arrow>
                  <Box sx={{ display: 'flex' }}>
                    <LocationWarningIcon />
                  </Box>
                </Tooltip>
              )}
            </Stack>
            <Typography sx={{ fontWeight: '700', ...disabledFieldStyle }}>Cross Street</Typography>
            <Stack direction="row" alignItems="center" sx={{ gap: 'var(--spacing-2xs)' }}>
              <Typography sx={{ ...disabledFieldStyle }}>{location?.address?.crossStreet || MISSING_VALUE}</Typography>
              {hasCrossStreetDifference && (
                <Tooltip title={getTooltip(mspIdentifier, mspLocation?.address.crossStreet)} arrow>
                  <Box sx={{ display: 'flex' }}>
                    <LocationWarningIcon />
                  </Box>
                </Tooltip>
              )}
            </Stack>
          </Stack>
        </Grid>
        <Grid size={4} />
      </Grid>
    </Stack>
  )
}
